import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useNavigate } from '@tanstack/react-router';
import {
  useProjects,
  useCreateProject,
  useProjectTemplates,
  type ProjectSummaryDto,
} from '../api/queries/projects';
import { useProjectStore } from '../stores/project.store';
import { FilterableSelect } from '../components/common/FilterableSelect';

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function statusColor(
  status: string | null,
): 'default' | 'primary' | 'success' | 'warning' | 'info' {
  switch (status?.toLowerCase()) {
    case 'drafting':
      return 'primary';
    case 'planning':
      return 'info';
    case 'complete':
      return 'success';
    case 'revising':
      return 'warning';
    default:
      return 'default';
  }
}

function ProjectCard({ project }: { project: ProjectSummaryDto }) {
  const navigate = useNavigate();
  const setActiveProject = useProjectStore((s) => s.setActiveProject);

  const handleClick = () => {
    setActiveProject(project.id);
    navigate({ to: '/projects/$projectId', params: { projectId: project.id } });
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea onClick={handleClick} sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6" noWrap>
            {project.name}
          </Typography>
          {project.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {project.description}
            </Typography>
          )}
          {project.status && (
            <Box>
              <Chip
                label={project.status}
                size="small"
                color={statusColor(project.status)}
              />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary">
            {project.bookCount} draft{project.bookCount !== 1 ? 's' : ''} /{' '}
            {project.outlineCount} outline
            {project.outlineCount !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Updated {formatRelativeTime(project.updatedAt)}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function CreateProjectDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('');
  const { data: templates = [] } = useProjectTemplates();
  const createProject = useCreateProject();
  const navigate = useNavigate();
  const setActiveProject = useProjectStore((s) => s.setActiveProject);

  const templateOptions = templates.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  const handleCreate = () => {
    createProject.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        templateId: templateId || undefined,
      },
      {
        onSuccess: (data) => {
          setActiveProject(data.id);
          navigate({
            to: '/projects/$projectId',
            params: { projectId: data.id },
          });
          handleClose();
        },
      },
    );
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setTemplateId('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Project</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
        }}
      >
        <TextField
          label="Project Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          size="small"
          autoFocus
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={3}
          fullWidth
          size="small"
        />
        <FilterableSelect
          options={templateOptions}
          value={templateId}
          onChange={setTemplateId}
          label="Template"
          placeholder="Choose a template (optional)"
          allowEmpty
          emptyLabel="No template"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!name.trim() || createProject.isPending}
        >
          {createProject.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ProjectListRoute() {
  const { data: projects = [], isLoading } = useProjects();
  const [createOpen, setCreateOpen] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">Loading projects...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          My Projects
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          New Project
        </Button>
      </Box>

      {projects.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <FolderOpenIcon
            sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
          />
          <Typography variant="h5" gutterBottom>
            Create your first project
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Start with a blank project or choose from a template to get started
            quickly.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Create Project
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid key={project.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <ProjectCard project={project} />
            </Grid>
          ))}
        </Grid>
      )}

      <CreateProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
